<template>
  <div class="mt-2" :class="{ 'conversation-details-embedded': embedded }">
    <div v-if="!viewModel.selectedConversation" class="card">
      <div class="card-body">
        <h5 class="card-title">Conversation Details</h5>
        <p class="card-text">
          This is a placeholder for the conversation details view. 
          When a conversation is selected from the list, this tab will show the full conversation content.
        </p>
        <div class="alert alert-info">
          <strong>Note:</strong> This tab is currently disabled until a conversation is selected.
        </div>
      </div>
    </div>
    
    <div v-else class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-start">
          <div class="d-flex flex-column flex-grow-1">
            <h5 class="card-title mb-2">{{ viewModel.selectedConversation.conversationContext?.title || 'Untitled Conversation' }}</h5>
            <div class="metadata-inline mb-2">
              <span class="me-3"><strong>ID:</strong> {{ viewModel.selectedConversation.conversationId }}</span>
              <span class="me-3"><strong>User:</strong> {{ getUserName(viewModel.selectedConversation.userId) || 'N/A' }}</span>
              <span class="me-3"><strong>Customer:</strong> {{ getCustomerName(viewModel.selectedConversation.customerId) || 'N/A' }}</span>
              <span class="me-3"><strong>Model:</strong> {{ viewModel.selectedConversation.model || 'N/A' }}</span>
              <span class="me-3"><strong>Created:</strong> {{ formatTimestamp(viewModel.selectedConversation.createdAt) }}</span>
              <span class="me-3"><strong>Type:</strong> {{ formatInitiationType(viewModel.selectedConversation.metaData?.initiationType) || 'N/A' }}</span>
            </div>
            <div class="metadata-inline">
              <span class="me-3"><strong>Interactions:</strong> {{ getStatValue('interactions', 8) }}</span>
              <span class="me-3"><strong>Total Tokens:</strong> {{ formatNumber(getStatValue('totalTokens', 0)) }} tokens</span>
              <span class="me-3"><strong>Total Tool Calls:</strong> {{ getStatValue('totalToolCalls', 0) }}</span>
              <span class="me-3"><strong>Avg Response:</strong> {{ getStatValue('avgResponse', '2.3s') }}</span>
              <span class="me-3"><strong>Total Duration:</strong> {{ getStatValue('totalDuration', '8m 42s') }}</span>
              <span class="me-3"><strong>Positive FB:</strong> {{ getStatValue('positiveFb', 4) }}</span>
              <span><strong>Negative FB:</strong> {{ getStatValue('negativeFb', 2) }}</span>
            </div>
            
            <!-- Quick Jump Navigation -->
            <div v-if="!collapsed" class="quick-jump-navigation mt-2">
              <div class="d-flex flex-wrap align-items-center gap-2">
                <span class="me-2 text-muted small"><strong>Jump to:</strong></span>
                <div class="d-flex flex-wrap gap-1">
                  <button 
                    v-for="(interaction, index) in sortedInteractions" 
                    :key="interaction.interactionId"
                    class="btn btn-sm"
                    :class="getJumpButtonClass(index)"
                    @click="jumpToInteraction(index)"
                    :title="`Jump to Turn ${index + 1}: ${getInteractionPreview(interaction)}`"
                  >
                    Turn {{ index + 1 }}
                    <span v-if="hasFeedback(interaction)" class="feedback-indicator ms-1">💬</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button v-if="collapsible" class="btn btn-outline-secondary btn-sm me-2" @click="$emit('toggleCollapsed')">
            <i class="fas fa-chevron-down me-1" v-if="!collapsed"></i>
            <i class="fas fa-chevron-up me-1" v-else></i>
            {{ collapsed ? 'Expand' : 'Collapse' }}
          </button>
          <button v-if="!collapsed" class="btn btn-outline-secondary btn-sm me-2" @click="toggleAnalysisPanel">
            <i class="fas fa-robot me-1"></i> Analyze
          </button>
          <button class="btn btn-outline-secondary btn-sm me-2" @click="copyAllMessages">
            <i class="fas fa-copy me-1"></i> Copy All Messages
          </button>
          <button v-if="showBackButton" class="btn btn-outline-secondary btn-sm" @click="goBackToList">
            <i class="fas fa-arrow-left me-1"></i> Back to List
          </button>
        </div>
      </div>
      <div v-if="!collapsed" class="card-body">
        <!-- Conversation Cards Section -->
        <div class="conversation-content-layout">
          <div class="row g-3">
            <!-- Main Conversation Column -->
            <div :class="showAnalysisPanel ? 'col-lg-8' : 'col-12'">
              <div class="conversation-cards-container" :class="{ 'conversation-cards-container-embedded': embedded }">
                <div v-if="!viewModel.conversationDetails?.interactions" class="alert alert-info">
                  No conversation data available.
                </div>
                
                <div v-else class="conversation-flow">
            <div 
              v-for="(interaction, index) in sortedInteractions" 
              :key="interaction.interactionId"
              class="conversation-turn mb-3"
              :data-interaction-id="interaction.interactionId"
            >
              <!-- User Prompt Card -->
              <div class="conversation-card user-prompt">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="turn-info">
                      <span class="turn-number">User Prompt #{{ index + 1 }}</span>
                    </div>
                    <div class="d-flex align-items-center">
                      <div class="timestamp me-2">
                        {{ formatTimestamp(interaction.createdAt) }}
                      </div>
                      <button 
                        class="btn btn-sm btn-outline-secondary me-1"
                        @click="copyUserPrompt(interaction)"
                        title="Copy User Prompt Text"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyUserPromptJson(interaction)"
                        title="Copy User Prompt JSON"
                      >
                        <strong>JSON</strong>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="card-content">
                  {{ interaction.userPrompt }}
                </div>
              </div>

              <!-- AI Response Card -->
              <div class="conversation-card ai-response">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="turn-info">
                      <span class="turn-number">AI Response #{{ index + 1 }}</span>
                    </div>
                    <div class="d-flex align-items-center">
                      <div class="timestamp me-2">
                        {{ formatTimestamp(interaction.createdAt) }}
                      </div>
                      <button 
                        class="btn btn-sm btn-outline-secondary me-1"
                        @click="copyAiResponse(interaction)"
                        title="Copy AI Response Text"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyAiResponseJson(interaction)"
                        title="Copy AI Response JSON"
                      >
                        <strong>JSON</strong>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="card-content">
                  <div v-for="response in getAssistantResponses(interaction.llmResponse)" :key="response.id">
                    <div v-if="response.content && response.content.length > 0">
                      <div v-for="content in response.content" :key="content.type">
                        <div v-if="content.type === 'text' && content.text" class="markdown-content" v-html="parseMarkdown(content.text)">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card-footer">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <div class="metrics me-3">
                        <span class="metric-item me-2">{{ formatNumber(interaction.totalTokens) }} tokens</span>
                        <span class="metric-item me-2">{{ formatResponseTime(interaction.responseTime) }}</span>
                        <span class="metric-item me-2">{{ getToolCalls(interaction.llmResponse).length }} tool calls</span>
                      </div>
                      <button 
                        v-for="(promptId, index) in interaction.systemPromptIds" 
                        :key="`${promptId}-${index}`"
                        v-if="promptId !== 0"
                        class="btn btn-sm btn-outline-secondary me-2"
                        @click="showSystemPromptModal(promptId, index, interaction)"
                      >
                        {{ getPromptLabel(index) }} : {{ promptId }}
                      </button>
                    </div>
                    <div class="modal-buttons">
                      <button
                        v-if="showFooterCollapseButton(index)"
                        class="btn btn-sm btn-outline-secondary"
                        @click="$emit('toggleCollapsed')"
                      >
                        <i class="fas fa-chevron-up me-1"></i>
                        Collapse Conversation
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-secondary" 
                        @click="toggleResponseDetails(interaction)"
                      >
                        <i class="fas fa-chevron-down me-1" v-if="!interaction.showDetails"></i>
                        <i class="fas fa-chevron-up me-1" v-else></i>
                        Response Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Response Details Cards (shown when expanded) -->
              <div v-if="interaction.showDetails" class="response-details">
                <!-- Request Context Card -->
                <div class="detail-card request-context">
                  <div class="card-header">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="turn-info">
                        <span class="turn-number">Request Context</span>
                      </div>
                      <button 
                        v-if="hasRequestContext(interaction)"
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyRequestContext(interaction)"
                        title="Copy Request Context"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                    </div>
                  </div>
                  <div class="card-content">
                    <div v-if="hasRequestContext(interaction)">
                      <pre class="json-content">{{ JSON.stringify(interaction.requestContext, null, 2) }}</pre>
                    </div>
                    <div v-else class="no-data-message">
                      No request context data for this interaction
                    </div>
                  </div>
                </div>

                <!-- Feature Context Card -->
                <div class="detail-card feature-context">
                  <div class="card-header">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="turn-info">
                        <span class="turn-number">Feature Context</span>
                      </div>
                      <button 
                        v-if="hasFeatureContext(interaction)"
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyFeatureContext(interaction)"
                        title="Copy Feature Context"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                    </div>
                  </div>
                  <div class="card-content">
                    <div v-if="hasFeatureContext(interaction)">
                      <!-- Feature context data will go here -->
                      <pre>{{ JSON.stringify(interaction.featureContext, null, 2) }}</pre>
                    </div>
                    <div v-else class="no-data-message">
                      No feature context data for this interaction
                    </div>
                  </div>
                </div>

                <!-- Metadata Card -->
                <div class="detail-card metadata">
                  <div class="card-header">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="turn-info">
                        <span class="turn-number">Metadata</span>
                      </div>
                      <button 
                        v-if="hasMetadata(interaction)"
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyMetadata(interaction)"
                        title="Copy Metadata"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                    </div>
                  </div>
                  <div class="card-content">
                    <div v-if="hasMetadata(interaction)">
                      <!-- Metadata will go here -->
                      <pre>{{ JSON.stringify(interaction.metaData, null, 2) }}</pre>
                    </div>
                    <div v-else class="no-data-message">
                      No metadata for this interaction
                    </div>
                  </div>
                </div>

                <!-- Tool Calls Section -->
                <div v-if="hasToolCalls(interaction.llmResponse)" class="tool-calls-section">
                  <div class="detail-card tool-calls-header">
                    <div class="card-header">
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="turn-info">
                          <span class="turn-number">Tool Calls</span>
                        </div>
                        <button 
                          v-if="getToolCalls(interaction.llmResponse).length > 0"
                          class="btn btn-sm btn-outline-secondary"
                          @click="copyAllToolCalls(interaction)"
                          title="Copy All Tool Calls"
                        >
                          <font-awesome-icon :icon="faCopy" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Individual Tool Call Groups -->
                  <div class="tool-calls-content">
                    <div 
                      v-for="toolCall in getToolCalls(interaction.llmResponse)" 
                      :key="toolCall.toolCallId"
                      class="tool-call-group"
                    >
                      <!-- Tool Call Card -->
                      <div class="detail-card tool-call">
                        <div class="card-header">
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="turn-info">
                              <span class="turn-number">Tool Call: {{ formatToolName(toolCall.toolName) }}</span>
                            </div>
                            <button 
                              class="btn btn-sm btn-outline-secondary"
                              @click="copyToolCall(toolCall)"
                              title="Copy Tool Call"
                            >
                              <font-awesome-icon :icon="faCopy" />
                            </button>
                          </div>
                        </div>
                        <div class="card-content">
                          <div class="tool-call-details">
                            <div class="tool-call-id">
                              <strong>Tool Call ID:</strong> {{ toolCall.toolCallId }}
                            </div>
                            <div class="tool-call-args">
                              <strong>Arguments:</strong>
                              <pre>{{ JSON.stringify(toolCall.args, null, 2) }}</pre>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Tool Response Card (nested underneath) -->
                      <div class="detail-card tool-response">
                        <div class="card-header">
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="turn-info">
                              <span class="turn-number">Tool Response: {{ formatToolName(toolCall.toolName) }}</span>
                            </div>
                            <button 
                              v-if="getToolResult(interaction.llmResponse, toolCall.toolCallId)"
                              class="btn btn-sm btn-outline-secondary"
                              @click="copyToolResponse(interaction, toolCall.toolCallId)"
                              title="Copy Tool Response"
                            >
                              <font-awesome-icon :icon="faCopy" />
                            </button>
                          </div>
                        </div>
                        <div class="card-content">
                          <div class="tool-response-details">
                            <div class="tool-response-result">
                              <strong>Result:</strong>
                              <pre>{{ JSON.stringify(getToolResult(interaction.llmResponse, toolCall.toolCallId), null, 2) }}</pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Feedback Card (only if feedback exists) -->
              <div v-if="interaction.feedback" :class="getFeedbackCardClass(interaction.feedback)">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="turn-info">
                      <span class="turn-number">Feedback</span>
                    </div>
                    <div class="timestamp">
                      {{ formatTimestamp(interaction.createdAt) }}
                    </div>
                  </div>
                </div>
                <div class="card-content">
                  <div class="feedback-content">
                    <div class="feedback-category">{{ interaction.feedback.category }}</div>
                    <div class="feedback-comment">{{ interaction.feedback.comment }}</div>
                  </div>
                </div>
              </div>
              <hr v-if="index < sortedInteractions.length - 1" class="interaction-separator" />
            </div>
                </div>
                <hr class="interaction-separator" />
              </div>
            </div>
            
            <!-- AI Chat Panel Column -->
            <div v-if="showAnalysisPanel" class="col-lg-4">
              <div class="ai-chat-panel-container">
                <AiChatPanel
                  title="Conversation Diagnostics"
                  :contextIds="conversationContext"
                  :customButtons="analysisButtons"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- System Prompt Modal -->
    <PromptViewModal
      v-if="showPromptModal"
      :show="showPromptModal"
      :loading="promptModalLoading"
      :type="promptModalHeader?.type"
      :title="promptModalHeader?.title"
      :featureType="promptModalHeader?.featureType"
      :version="promptModalHeader?.version"
      :order="promptModalHeader?.order"
      :isActive="promptModalHeader?.isActive"
      :createdAt="promptModalHeader?.createdAt"
      :content="promptModalData?.content"
      :description="promptModalData?.description"
      @close="onClosePromptModal"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import InsightsService from '../../services/InsightsService.js';
import PromptViewModal from '../../components/PromptViewModal.vue';
import AiChatPanel from '../../components/AiChatPanel.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { parseMarkdown } from '../../utils/markdown.js';
import { startCase } from 'lodash';

export default {
  name: "ConversationDetails",
  components: { PromptViewModal, FontAwesomeIcon, AiChatPanel },
  props: {
    viewModel: {
      type: Object,
      required: true,
    },
    showBackButton: {
      type: Boolean,
      default: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['backToList', 'toggleCollapsed'],
  data() {
    return {
      model: streetmetrics.model,
      insightsService: new InsightsService(),
      showPromptModal: false,
      promptModalLoading: false,
      promptModalData: null,
      promptModalHeader: null,
      showAnalysisPanel: false,
      faCopy,
    };
  },
  computed: {
    sortedInteractions() {
      if (!this.viewModel.conversationDetails?.interactions) return [];
      
      const interactions = [...this.viewModel.conversationDetails.interactions].sort((a, b) => {
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
      
      // Initialize showDetails property for each interaction
      interactions.forEach(interaction => {
        if (interaction.showDetails === undefined) {
          interaction.showDetails = false;
        }
      });
      
      return interactions;
    },
    conversationContext() {
      if (!this.viewModel.selectedConversation) {
        return [];
      }
      
      return [parseInt(this.viewModel.selectedConversation.conversationId)];
    },
    analysisButtons() {
      const systemPrompt = `You are analyzing a StreetMetrics conversation where users interact with an AI assistant for OOH advertising planning and analysis. The platform includes Smart Search for inventory discovery, Manual Filtering, Inventory analysis, Campaign Reporting, and Scenarios planning.
      Provide specific evidence from the conversation with brief, actionable insights. Focus only on what actually happened in the conversation - do not speculate about potential improvements or suggest features the user didn't explicitly request.
      CRITICAL: The filterFrames tool returning empty results is EXPECTED and SUCCESSFUL when user criteria are specific. When the AI responds to empty results by suggesting to relax filters or try different criteria, this is CORRECT AI behavior and should be considered a success, not a problem. Do not flag these interactions as tool mismatches, user confusion, or failures unless there are actual errors or the AI provides unhelpful guidance.`
      return [
        {
          label: 'What went well?',
          userPrompt: `Identify the key successes in this conversation. What worked effectively - tool usage, user guidance, goal achievement, or workflow navigation?`
        },
        {
          label: 'What went wrong?',
          userPrompt: `Identify actual problems in this conversation - tool errors, system failures, unhelpful AI responses, or genuine user confusion. Do not flag normal workflow outcomes like empty search results that were handled appropriately by the AI.`
        },
        {
          label: 'Data gaps',
          userPrompt: `Did the user explicitly request specific data types, metrics, or information that StreetMetrics doesn't currently provide? Answer "No" if no missing data was requested. If "Yes," quote the exact user request and identify what specific data type was unavailable.`
        },
        {
          label: 'Functionality gaps',
          userPrompt: `Did the user explicitly request platform features, tools, or capabilities that don't currently exist? Answer "No" if no missing functionality was requested. If "Yes," quote the exact user request and identify what specific feature was unavailable.`
        },
        {
          label: 'How can we improve?',
          userPrompt: `Based on what actually happened in this conversation, what are the top 2-3 specific improvements we could make to better serve this user's needs?`
        },
      ].map(button => ({
        ...button,
        systemPrompt,
      }));
    },
  },
  mounted() {
  },
  methods: {
    parseMarkdown(text) {
      return parseMarkdown(text);
    },
    toggleAnalysisPanel() {
      this.showAnalysisPanel = !this.showAnalysisPanel;
    },
    formatTimestamp(timestamp) {
      return dayjs(timestamp).format('MMM D, YYYY h:mm A');
    },
    formatInitiationType(type) {
      if (!type) return '';
      return type.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    goBackToList() {
      this.$emit('backToList');
    },
    getStatValue(statType, defaultValue) {
      if (!this.viewModel.conversationDetails?.interactions) {
        return defaultValue;
      }
      
      const interactions = this.viewModel.conversationDetails.interactions;
      
      switch (statType) {
        case 'interactions':
          return interactions.length;
          
        case 'totalTokens':
          return interactions.reduce((total, interaction) => {
            return total + (interaction.totalTokens || 0);
          }, 0);
          
        case 'totalToolCalls':
          return interactions.reduce((total, interaction) => {
            const toolCalls = this.getToolCalls(interaction.llmResponse);
            return total + toolCalls.length;
          }, 0);
          
        case 'avgResponse':
          const responseTimes = interactions
            .map(interaction => interaction.responseTime)
            .filter(time => time && time > 0);
          
          if (responseTimes.length === 0) return '0ms';
          
          const avgTime = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;
          return this.formatResponseTime(avgTime);
          
        case 'totalDuration':
          if (interactions.length < 2) return '0s';
          
          const firstTime = new Date(interactions[0].createdAt);
          const lastTime = new Date(interactions[interactions.length - 1].createdAt);
          const durationMs = lastTime - firstTime;
          
          if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
          const minutes = Math.floor(durationMs / 60000);
          const seconds = Math.round((durationMs % 60000) / 1000);
          return `${minutes}m ${seconds}s`;
          
        case 'positiveFb':
          return interactions.filter(interaction => 
            interaction.feedback && interaction.feedback.toLowerCase().includes('positive')
          ).length;
          
        case 'negativeFb':
          return interactions.filter(interaction => 
            interaction.feedback && interaction.feedback.toLowerCase().includes('negative')
          ).length;
          
        default:
          return defaultValue;
      }
    },
    getAssistantResponses(llmResponse) {
      if (!llmResponse) return [];
      return llmResponse.filter(response => response.role === 'assistant');
    },
    getToolCallResponses(llmResponse) {
      if (!llmResponse) return [];
      
      const toolResponses = [];
      
      llmResponse.forEach(response => {
        if (response.role === 'assistant' && response.content) {
          // Find tool calls in assistant responses
          response.content.forEach(content => {
            if (content.type === 'tool-call') {
              toolResponses.push({
                type: 'tool-call',
                toolName: content.toolName,
                toolCallId: content.toolCallId,
                args: content.args,
                responseId: response.id
              });
            }
          });
        } else if (response.role === 'tool' && response.content) {
          // Find tool results in tool responses
          response.content.forEach(content => {
            if (content.type === 'tool-result') {
              toolResponses.push({
                type: 'tool-result',
                toolName: content.toolName,
                toolCallId: content.toolCallId,
                result: content.result,
                responseId: response.id
              });
            }
          });
        }
      });
      
      return toolResponses;
    },
    getToolCalls(llmResponse) {
      if (!llmResponse) return [];
      const toolCalls = [];
      llmResponse.forEach(response => {
        if (response.role === 'assistant' && response.content) {
          response.content.forEach(content => {
            if (content.type === 'tool-call') {
              toolCalls.push(content);
            }
          });
        }
      });
      return toolCalls;
    },
    getToolResult(llmResponse, toolCallId) {
      if (!llmResponse) return {};
      
      const toolResponse = llmResponse.find(response => 
        response.role === 'tool' && response.content && response.content.some(content => content.toolCallId === toolCallId)
      );
      
      if (toolResponse && toolResponse.content) {
        const content = toolResponse.content.find(content => content.toolCallId === toolCallId);
        if (content && content.result) {
          // If result is a string, try to parse it as JSON
          if (typeof content.result === 'string') {
            try {
              return JSON.parse(content.result);
            } catch (e) {
              // If it's not valid JSON, return as is
              return content.result;
            }
          }
          // If result is already an object, return it directly
          return content.result;
        }
      }
      return {};
    },
    formatToolName(toolName) {
      if (!toolName) return 'Unknown Tool';
      return toolName.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    hasToolCalls(llmResponse) {
      if (!llmResponse) return false;
      return llmResponse.some(response => 
        response.role === 'assistant' && 
        response.content && 
        response.content.some(content => content.type === 'tool-call')
      );
    },
    hasRequestContext(interaction) {
      return interaction.requestContext && Object.keys(interaction.requestContext).length > 0;
    },
    hasFeatureContext(interaction) {
      return interaction.featureContext && Object.keys(interaction.featureContext).length > 0;
    },
    hasMetadata(interaction) {
      return interaction.metaData && Object.keys(interaction.metaData).length > 0;
    },
    formatNumber(num) {
      if (!num) return '0';
      return num.toLocaleString();
    },
    formatResponseTime(ms) {
      if (!ms) return '0ms';
      if (ms < 1000) return `${ms}ms`;
      return `${(ms / 1000).toFixed(1)}s`;
    },
    getPromptLabel(index) {
      // V3+ structure: [base, context, workflow, org, fragment1, fragment2, ...]
      if (index === 0) return 'Base';
      if (index === 1) return 'Context';
      if (index === 2) return 'Workflow';
      if (index === 3) return 'Org';
      // Index 4+ are user fragments
      const fragmentNumber = index - 3;
      return `User ${fragmentNumber}`;
    },
    formatPromptType(promptType) {
      if (!promptType) return '';
      // startCase handles both camelCase (InsightStructure) and snake_case (insight_structure)
      return startCase(String(promptType).replace(/_/g, ' '));
    },
    getFeedbackCardClass(feedback) {
      const isPositive = feedback.category?.toLowerCase().includes('positive') || 
                        feedback.comment?.toLowerCase().includes('positive') ||
                        feedback.comment?.toLowerCase().includes('good') ||
                        feedback.comment?.toLowerCase().includes('great') ||
                        feedback.comment?.toLowerCase().includes('excellent');
      
      return `conversation-card feedback ${isPositive ? 'feedback-positive' : 'feedback-negative'}`;
    },
    async showSystemPromptModal(promptId, index, interaction) {
      this.showPromptModal = true;
      this.promptModalLoading = true;
      this.promptModalData = null;
      this.promptModalHeader = null;
      
      const promptLabel = this.getPromptLabel(index);
      
      try {
        // Handle different prompt types based on index
        if (index === 0 || index === 1 || index === 2) {
          // System prompts (Base, Context, Workflow)
          const fullPrompt = await this.insightsService.getSystemPromptContent(promptId);
          
          this.promptModalHeader = {
            type: 'system',
            title: fullPrompt.promptName ? `${fullPrompt.promptName} System Prompt` : `${promptLabel} System Prompt (ID: ${promptId})`,
            featureType: fullPrompt.promptType ? this.formatPromptType(fullPrompt.promptType) : promptLabel.toLowerCase(),
            version: fullPrompt.version || null,
            isActive: fullPrompt.isActive !== undefined ? fullPrompt.isActive : true,
            createdAt: fullPrompt.createdAt ? this.formatTimestamp(fullPrompt.createdAt) : null,
          };
          
          this.promptModalData = {
            content: fullPrompt.promptContent || 'No content available',
          };
        } else if (index === 3) {
          // Organization prompt
          const orgPrompt = await this.insightsService.getOrganizationPromptById(promptId);
          
          const customerName = this.getCustomerName(orgPrompt.customerId) || `Customer ${orgPrompt.customerId}`;
          
          this.promptModalHeader = {
            type: 'organization',
            title: `${promptLabel} Prompt (ID: ${promptId})`,
            featureType: customerName,
            version: orgPrompt.version || null,
            isActive: orgPrompt.isActive !== undefined ? orgPrompt.isActive : true,
            createdAt: orgPrompt.createdAt ? this.formatTimestamp(orgPrompt.createdAt) : null,
          };
          
          this.promptModalData = {
            content: orgPrompt.promptContent || 'No content available',
          };
        } else {
          // User fragment prompts (index 4+)
          const fragment = await this.insightsService.getPromptFragmentById(promptId);
          
          if (fragment) {
            const fragmentTypeLabel = fragment.promptType ? this.formatPromptType(fragment.promptType) : 'Fragment';
            const fragmentNameLabel = fragment.promptName ? this.formatPromptType(fragment.promptName) : '';
            const featureTypeLabel = fragmentNameLabel 
              ? `${fragmentTypeLabel} - ${fragmentNameLabel}` 
              : fragmentTypeLabel;
            
            this.promptModalHeader = {
              type: 'fragment',
              title: `${promptLabel} Fragment (ID: ${promptId})`,
              featureType: featureTypeLabel,
              version: fragment.version || null,
              isActive: fragment.isActive !== undefined ? fragment.isActive : true,
              createdAt: fragment.createdAt ? this.formatTimestamp(fragment.createdAt) : null,
            };
            
            this.promptModalData = {
              content: fragment.promptContent || 'No content available',
              description: fragment.description || null,
            };
          } else {
            throw new Error('Fragment not found');
          }
        }
      } catch (error) {
        console.error('Error fetching prompt content:', error);
        this.promptModalHeader = {
          type: index === 3 ? 'organization' : (index >= 4 ? 'fragment' : 'system'),
          title: `${promptLabel} Prompt (ID: ${promptId})`,
          featureType: promptLabel.toLowerCase(),
          version: null,
          isActive: true,
          createdAt: null,
        };
        this.promptModalData = {
          content: 'Error loading prompt content. Please try again.',
        };
      } finally {
        this.promptModalLoading = false;
      }
    },
    onClosePromptModal() {
      this.showPromptModal = false;
      this.promptModalData = null;
      this.promptModalHeader = null;
    },
    toggleResponseDetails(interaction) {
      interaction.showDetails = !interaction.showDetails;
    },
    async copyRequestContext(interaction) {
      if (this.hasRequestContext(interaction)) {
        try {
          const jsonString = JSON.stringify(interaction.requestContext, null, 2);
          await navigator.clipboard.writeText(jsonString);
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'Request Context copied to clipboard!';
            this.model.toast.background = 'bg-success';
          });
        } catch (error) {
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'Failed to copy Request Context';
            this.model.toast.background = 'bg-danger';
          });
        }
      } else {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'No request context data to copy';
          this.model.toast.background = 'bg-warning';
        });
      }
    },
    async copyFeatureContext(interaction) {
      if (this.hasFeatureContext(interaction)) {
        try {
          const jsonString = JSON.stringify(interaction.featureContext, null, 2);
          await navigator.clipboard.writeText(jsonString);
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'Feature Context copied to clipboard!';
            this.model.toast.background = 'bg-success';
          });
        } catch (error) {
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'Failed to copy Feature Context';
            this.model.toast.background = 'bg-danger';
          });
        }
      } else {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'No feature context data to copy';
          this.model.toast.background = 'bg-warning';
        });
      }
    },
    async copyMetadata(interaction) {
      if (this.hasMetadata(interaction)) {
        try {
          const jsonString = JSON.stringify(interaction.metaData, null, 2);
          await navigator.clipboard.writeText(jsonString);
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'Metadata copied to clipboard!';
            this.model.toast.background = 'bg-success';
          });
        } catch (error) {
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'Failed to copy Metadata';
            this.model.toast.background = 'bg-danger';
          });
        }
      } else {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'No metadata to copy';
          this.model.toast.background = 'bg-warning';
        });
      }
    },
    async copyAllToolCalls(interaction) {
      try {
        const toolCalls = this.getToolCalls(interaction.llmResponse);
        const completeToolData = toolCalls.map(toolCall => {
          const toolResult = this.getToolResult(interaction.llmResponse, toolCall.toolCallId);
          return {
            toolCall: toolCall,
            toolResponse: toolResult
          };
        });
        const allToolDataString = JSON.stringify(completeToolData, null, 2);
        await navigator.clipboard.writeText(allToolDataString);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'All Tool Calls and Responses copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy Tool Calls';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    async copyToolCall(toolCall) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(toolCall, null, 2));
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Tool Call copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy Tool Call';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    async copyToolResponse(interaction, toolCallId) {
      try {
        const toolResult = this.getToolResult(interaction.llmResponse, toolCallId);
        await navigator.clipboard.writeText(JSON.stringify(toolResult, null, 2));
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Tool Response copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy Tool Response';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    async copyUserPrompt(interaction) {
      try {
        const userName = this.getUserName(interaction.userId) || 'Unknown User';
        const textContent = typeof interaction.userPrompt === 'string' 
          ? interaction.userPrompt 
          : JSON.stringify(interaction.userPrompt);
        
        const copyText = `${userName}: "${textContent}"`;
        await navigator.clipboard.writeText(copyText);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'User Prompt text copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy User Prompt';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    async copyUserPromptJson(interaction) {
      try {
        const userPromptObject = {
          userPrompt: interaction.userPrompt
        };
        const jsonString = JSON.stringify(userPromptObject, null, 2);
        await navigator.clipboard.writeText(jsonString);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'User Prompt JSON copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy User Prompt JSON';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    async copyAiResponse(interaction) {
      try {
        const assistantResponses = this.getAssistantResponses(interaction.llmResponse);
        if (assistantResponses.length > 0) {
          const lastAssistantResponse = assistantResponses[assistantResponses.length - 1];
          let textContent = '';
          
          if (lastAssistantResponse.content && lastAssistantResponse.content.length > 0) {
            textContent = lastAssistantResponse.content
              .filter(content => content.type === 'text' && content.text)
              .map(content => content.text)
              .join('\n\n');
          }
          
          if (textContent.trim()) {
            const copyText = `AI Assistant: "${textContent}"`;
            await navigator.clipboard.writeText(copyText);
            this.model.toast.message = null;
            this.$nextTick(() => {
              this.model.toast.message = 'AI Response text copied to clipboard!';
              this.model.toast.background = 'bg-success';
            });
          } else {
            this.model.toast.message = null;
            this.$nextTick(() => {
              this.model.toast.message = 'No AI response text content to copy';
              this.model.toast.background = 'bg-warning';
            });
          }
        } else {
          this.model.toast.message = null;
          this.$nextTick(() => {
            this.model.toast.message = 'No AI response content to copy';
            this.model.toast.background = 'bg-warning';
          });
        }
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy AI Response';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    async copyAiResponseJson(interaction) {
      try {
        const jsonString = JSON.stringify(interaction, null, 2);
        await navigator.clipboard.writeText(jsonString);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'AI Response JSON copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy AI Response JSON';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    getUserName(userId) {
      if (!userId || !this.model.users) return null;
      const user = this.model.users.find(u => u.userId === userId);
      return user ? user.displayName : null;
    },
    getCustomerName(customerId) {
      if (!customerId || !this.model.customers) return null;
      const customer = this.model.customers.find(c => c.customerId === customerId);
      return customer ? customer.customerName : null;
    },
    getInteractionPreview(interaction) {
      if (!interaction) return 'No interaction data';
      if (interaction.userPrompt) return `User Prompt: "${interaction.userPrompt.substring(0, 50)}..."`;
      if (interaction.llmResponse && interaction.llmResponse.length > 0) {
        const lastAssistantResponse = this.getAssistantResponses(interaction.llmResponse)[0];
        if (lastAssistantResponse && lastAssistantResponse.content && lastAssistantResponse.content.length > 0) {
          const lastContent = lastAssistantResponse.content[0];
          if (lastContent.type === 'text' && lastContent.text) {
            return `AI Response: "${lastContent.text.substring(0, 50)}..."`;
          }
        }
      }
      return 'Interaction details';
    },
    getJumpButtonClass(index) {
      const interaction = this.sortedInteractions[index];
      if (interaction.feedback) {
        return 'btn-outline-info';
      }
      return 'btn-outline-secondary';
    },
    jumpToInteraction(index) {
      const interaction = this.sortedInteractions[index];
      if (interaction) {
        this.$nextTick(() => {
          // Find the interaction element and scroll to it
          const interactionElement = this.$el.querySelector(`[data-interaction-id="${interaction.interactionId}"]`);
          if (interactionElement) {
            interactionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    hasFeedback(interaction) {
      return interaction.feedback && (
        (typeof interaction.feedback === 'string' && interaction.feedback.trim() !== '') ||
        (typeof interaction.feedback === 'object' && Object.keys(interaction.feedback).length > 0)
      );
    },
    getFeedbackCount(interaction) {
      if (!interaction.feedback) return 0;
      if (typeof interaction.feedback === 'string') {
        return 1;
      }
      return 1; // Assuming each feedback object is a single item
    },
    hasAnyFeedback() {
      return this.sortedInteractions.some(interaction => this.hasFeedback(interaction));
    },
    showFooterCollapseButton(index) {
      return this.collapsible && !this.collapsed && index === this.sortedInteractions.length - 1;
    },
    totalFeedbackCount() {
      return this.sortedInteractions.reduce((sum, interaction) => sum + this.getFeedbackCount(interaction), 0);
    },
    jumpToFeedback() {
      const firstFeedbackInteraction = this.sortedInteractions.find(interaction => this.hasFeedback(interaction));
      if (firstFeedbackInteraction) {
        this.$nextTick(() => {
          // Find the feedback element and scroll to it
          const feedbackElement = this.$el.querySelector(`[data-interaction-id="${firstFeedbackInteraction.interactionId}"]`);
          if (feedbackElement) {
            feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    async copyAllMessages() {
      try {
        const conversation = this.viewModel.selectedConversation;
        const userName = this.getUserName(conversation.userId) || 'Unknown User';
        const customerName = this.getCustomerName(conversation.customerId) || 'Unknown Company';
        const conversationTitle = conversation.conversationContext?.title || 'Untitled Conversation';
        const conversationTime = this.formatTimestamp(conversation.createdAt);
        
        let conversationText = `${conversationTitle}\n${userName} - ${customerName}\n${conversationTime}\n\n`;
        
        this.sortedInteractions.forEach((interaction, index) => {
          const turnNumber = index + 1;
          
          const userPrompt = interaction.userPrompt || 'N/A';
          
          let aiResponse = 'N/A';
          if (interaction.llmResponse && interaction.llmResponse.length > 0) {
            const assistantResponses = this.getAssistantResponses(interaction.llmResponse);
            if (assistantResponses.length > 0) {
              const lastResponse = assistantResponses[assistantResponses.length - 1];
              if (lastResponse.content && lastResponse.content.length > 0) {
                aiResponse = lastResponse.content
                  .filter(content => content.type === 'text' && content.text)
                  .map(content => content.text)
                  .join('\n\n');
              }
            }
          }
          
          conversationText += `${userName} #${turnNumber}: ${userPrompt}\n`;
          conversationText += `AI Assistant #${turnNumber}: ${aiResponse}\n`;
          
          if (index < this.sortedInteractions.length - 1) {
            conversationText += '\n-----\n\n';
          }
        });
        
        await navigator.clipboard.writeText(conversationText);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'All messages copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        console.error('Error copying all messages:', error);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy all messages';
          this.model.toast.background = 'bg-danger';
        });
      }
    }
  },
};
</script>

<style>
@import '../../assets/css/markdown.scss';
</style>

<style scoped>
.card {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.card-title {
  color: #495057;
  font-weight: 600;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.metadata-inline {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.2;
}

.stats-row {
  margin-top: 1.5rem;
}

@media (max-width: 768px) {
  .stat-card {
    min-width: 100px;
    padding: 0.75rem;
  }
  
  .stat-value {
    font-size: 1.25rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
}

.stat-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  min-width: 120px;
  max-width: 160px;
  flex: 1 1 auto;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Stat card color themes */
.stat-card.interactions .stat-value {
  color: #007bff;
}

.stat-card.total-tokens .stat-value {
  color: #17a2b8;
}

.stat-card.avg-response .stat-value {
  color: #28a745;
}

.stat-card.total-duration .stat-value {
  color: #ffc107;
}

.stat-card.positive-fb .stat-value {
  color: #28a745;
}

.stat-card.negative-fb .stat-value {
  color: #dc3545;
}

/* Conversation Cards */
.conversation-cards {
  max-width: 100%;
  overflow-x: hidden;
}

.conversation-flow {
  max-width: 100%;
  overflow-x: hidden;
}

.conversation-turn {
  max-width: 100%;
  overflow-x: hidden;
}

.conversation-content-layout {
  width: 100%;
}

.conversation-cards-container {
  height: 58vh;
  overflow-y: auto;
  padding-right: 1rem;
}

.conversation-details-embedded .conversation-cards-container-embedded {
  height: auto;
  overflow-y: visible;
  padding-right: 0;
}

.ai-chat-panel-container {
  width: 100%;
  height: 58vh;
}

.conversation-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-right: 1rem;
}

.conversation-card.user-prompt {
  border-left: 4px solid #007bff;
  background-color: #e3f2fd;
}

.conversation-card.ai-response {
  border-left: 4px solid #6f42c1;
  background-color: #f3e5f5;
}

.conversation-card.feedback-positive {
  border-left: 4px solid #28a745;
  background-color: #e8f5e8;
}

.conversation-card.feedback-negative {
  border-left: 4px solid #dc3545;
  background-color: #fde8e8;
}

.conversation-card .card-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
  background-color: rgba(255,255,255,0.7);
}

.conversation-card .card-content {
  padding: 1rem;
}

.conversation-card.user-prompt .card-content {
  background-color: #e3f2fd;
}

.conversation-card.ai-response .card-content {
  background-color: #f3e5f5;
}

.conversation-card.feedback-positive .card-content {
  background-color: #e8f5e8;
}

.conversation-card.feedback-negative .card-content {
  background-color: #fde8e8;
}

.conversation-card .card-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid #dee2e6;
  background-color: rgba(255,255,255,0.7);
}

.turn-number {
  font-weight: 600;
  color: #495057;
}

.timestamp {
  font-size: 0.8rem;
  color: #6c757d;
}

.metrics {
  display: flex;
  gap: 1rem;
}

.metric-item {
  font-size: 0.8rem;
  color: #6c757d;
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.modal-buttons {
  display: flex;
  gap: 0.5rem;
}

.modal-buttons .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}



.feedback-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.feedback-category {
  font-weight: 600;
  color: #28a745;
}

.feedback-comment {
  color: #495057;
}

/* Response Details Styling */
.response-details {
  margin-left: 2rem;
  margin-right: 1rem;
  margin-top: 1rem;
  max-width: calc(100% - 3rem);
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
}

.detail-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  max-width: 100%;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

.detail-card .card-header {
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  width: 100%;
  box-sizing: border-box;
}

.detail-card .card-content {
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.no-data-message {
  color: #6c757d;
  font-style: italic;
  text-align: center;
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.tool-calls-section {
  margin-top: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.tool-calls-content {
  margin-left: 1rem;
  width: calc(100% - 1rem);
  box-sizing: border-box;
  overflow-x: hidden;
}

.tool-call-group {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.tool-call {
  border-left: 4px solid #7c3aed;
  background-color: #f3f4f6;
  padding-left: 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  margin-bottom: 1rem;
}

.tool-call .card-header {
  background-color: rgba(124, 58, 237, 0.1);
  width: 100%;
  box-sizing: border-box;
}

.tool-call .card-content {
  background-color: #f3f4f6;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.tool-call .tool-call-details {
  font-size: 0.9rem;
  color: #495057;
  width: 100%;
  box-sizing: border-box;
}

.tool-call .tool-call-id {
  font-weight: 600;
  margin-bottom: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.tool-call .tool-call-args pre,
.tool-response .tool-response-result pre {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-size: 0.8rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 100%;
  overflow-wrap: break-word;
  width: 100%;
  box-sizing: border-box;
  word-break: break-all;
}

.tool-response {
  border-left: 4px solid #7c3aed;
  background-color: #f3f4f6;
  padding-left: 1rem;
  width: calc(100% - 1.5rem);
  box-sizing: border-box;
  overflow: hidden;
  margin-left: 1.5rem;
  margin-top: 0;
  margin-bottom: 1rem;
}

.tool-response .card-header {
  background-color: rgba(124, 58, 237, 0.1);
  width: 100%;
  box-sizing: border-box;
}

.tool-response .card-content {
  background-color: #f3f4f6;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.tool-response .tool-response-details {
  font-size: 0.9rem;
  color: #495057;
  width: 100%;
  box-sizing: border-box;
}

/* JSON Content Styling */
.json-content {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  overflow-x: hidden;
  color: #495057;
}

/* Detail Card Color Schemes */
.detail-card.request-context {
  background-color: #e3f2fd;
  border-left: 4px solid #17a2b8;
}

.detail-card.request-context .card-header {
  background-color: rgba(23, 162, 184, 0.1);
  border-bottom: 1px solid #17a2b8;
}

.detail-card.feature-context {
  background-color: #e0f2f1;
  border-left: 4px solid #00838f;
}

.detail-card.feature-context .card-header {
  background-color: rgba(0, 131, 143, 0.1);
  border-bottom: 1px solid #00838f;
}

.detail-card.metadata {
  background-color: #f8f9fa;
  border-left: 4px solid #6c757d;
}

.detail-card.metadata .card-header {
  background-color: rgba(108, 117, 125, 0.1);
  border-bottom: 1px solid #6c757d;
}

.detail-card.tool-calls-header {
  background-color: #f3f4f6;
  border-left: 4px solid #7c3aed;
}

.detail-card.tool-calls-header .card-header {
  background-color: rgba(124, 58, 237, 0.1);
  border-bottom: 1px solid #7c3aed;
}

/* Interaction Separator Styling */
.interaction-separator {
  border: none;
  border-top: 1px dashed #e0e0e0;
  margin: 1.5rem 0;
  width: 100%;
  box-sizing: border-box;
}

/* Quick Jump Navigation Styling */
.quick-jump-navigation {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
}

.quick-jump-navigation .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  min-width: 60px;
}

.feedback-indicator {
  font-size: 0.7rem;
}
</style>
